import { createElement, lazy } from 'react';
import type { ConfigApi, IdentityApi } from '@backstage/frontend-plugin-api';
import {
  ApiBlueprint,
  AppRootElementBlueprint,
  configApiRef,
  createApiRef,
  createFrontendPlugin,
  identityApiRef,
} from '@backstage/frontend-plugin-api';
import { datadogRum } from '@datadog/browser-rum';

export interface DatadogRumApi {
  readonly initialised: boolean;
}

export const datadogRumApiRef = createApiRef<DatadogRumApi>().with({
  id: 'ns.datadog-rum',
});

// base64url: URL-safe and carries no Datadog token (rum/ddsource/dd-api-key)
// for an adblocker to match. The proxy backend decodes it (see
// @ns/plugin-ns-datadog-rum-backend).
function encodeIntake(path: string, parameters: string): string {
  return btoa(`${path}?${parameters}`)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', ''); // base64 padding is always trailing; no regex needed
}

// Exported for direct testing of the side-effect without going through the
// extension wiring.
export function initialiseDatadogRum(
  configApi: ConfigApi,
  identityApi: IdentityApi,
): DatadogRumApi {
  const clientToken = configApi.getOptionalString('datadogRum.clientToken');
  if (!clientToken) {
    return { initialised: false };
  }

  const proxyUrl = configApi.getOptionalString('datadogRum.proxyUrl');

  datadogRum.init({
    applicationId: configApi.getString('datadogRum.applicationId'),
    clientToken,
    env: configApi.getString('datadogRum.env'),
    service: configApi.getString('datadogRum.service'),
    sessionReplaySampleRate: 0,
    sessionSampleRate: 100,
    site: configApi.getString('datadogRum.site'),
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    version: configApi.getString('datadogRum.version'),
    // Route intake via Backstage proxy, opaquely encoded so no Datadog token
    // appears in the URL for adblockers to match.
    ...(proxyUrl
      ? {
          proxy: (options: { parameters: string; path: string }) =>
            `${proxyUrl}?d=${encodeIntake(options.path, options.parameters)}`,
        }
      : {}),
  });

  identityApi.getBackstageIdentity().then(identity => {
    datadogRum.setUser({ id: identity.userEntityRef });
  });

  return { initialised: true };
}

const datadogRumApi = ApiBlueprint.make({
  name: 'rum',
  params: define =>
    define({
      api: datadogRumApiRef,
      deps: { configApi: configApiRef, identityApi: identityApiRef },
      factory: ({ configApi, identityApi }) =>
        initialiseDatadogRum(configApi, identityApi),
    }),
});

// API factories are lazy in NFS — without a useApi() consumer, the SDK init
// never fires. This root element mounts a tiny component that requests the
// API, forcing eager construction on app boot.
const datadogRumTrigger = AppRootElementBlueprint.make({
  name: 'trigger',
  params: {
    element: createElement(
      lazy(() =>
        import('./rum-trigger').then(m => ({ default: m.RumTrigger })),
      ),
    ),
  },
});

export default createFrontendPlugin({
  extensions: [datadogRumApi, datadogRumTrigger],
  pluginId: 'ns-datadog-rum',
});
